import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import { type ActionTypeOrganizationAdditionalInfoConfig, Endpoints } from '@/api';
import { EmptyState } from '@/components/EmptyState';
import RefashionLogo from '@/components/RefashionLogo';
import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import ConfirmationDialog from '@/design_system/Dialog/ConfirmationDialog';
import Drawer, { DrawerBody, DrawerHeader } from '@/design_system/Drawer';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import { useShowToast } from '@/design_system/Toast';
import Tooltip from '@/design_system/Tooltip';
import InlineText from '@/design_system/Typography/InlineText';
import Title from '@/design_system/Typography/Title';
import IconAdd from '@/icons/Add.svg';
import IconCheck from '@/icons/Check.svg';
import IconChevron from '@/icons/Chevron.svg';
import IconCross from '@/icons/Cross.svg';
import IconEdit from '@/icons/Edit.svg';
import IconTrash from '@/icons/Trash.svg';
import {
  RefashionAction,
  useActionTypesOrganization,
  useCreatePackActionType,
  useDeletePackActionType,
  useUpdatePackActionType,
} from '@/models/actionType';
import { I18nWording, SUPPORTED_LOCALES, useDatabaseTranslation } from '@/services/i18n';
import { isMissingTranslation } from '@/utils/isMissingTranslation';
import { trimI18nWording } from '@/utils/trimI18nWording';
import useViewPort from '@/utils/useViewport';

import { AgreementPricesDialog } from '../shared/AgreementPricesDialog';
import { BrandsMultiSelect } from '../shared/BrandsMultiSelect';
import { IsQuantifiableRadio } from '../shared/IsQuantifiableRadio';
import { NameInputs } from '../shared/NameInputs';
import { ProductAttributeValuesHierarchy } from '../shared/ProductAttributeValuesHierarchy';
import { ProductAttributeValuesMultiSelects } from '../shared/ProductAttributeValuesMultiSelects';

import { AdditionalInfoConfigInputs } from './shared/AdditionalInfoConfigInputs';
import { RefashionIdSelect } from './shared/RefashionIdSelect';
import { hasAdditionalInfoConfigError } from './utils/hasAdditionalInfoConfigError';
import { hasNameError } from './utils/hasNameError';

type PackActionType =
  Endpoints['GET /action-types/organization']['response']['packActionTypes'][number];

export const PackActionsTable = () => {
  const { t } = useLingui();
  const { _compare } = useDatabaseTranslation();

  const { data: { packActionTypes } = {}, isLoading } = useActionTypesOrganization();

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [packActionTypeEdit, setPackActionTypeEdit] = useState<PackActionType | null>(null);

  const sortedPackActionTypes = packActionTypes?.toSorted((a, b) => _compare(a.name, b.name));

  const columnWidths = {
    name: 'minmax(400px, auto)',
    productCategories: 'minmax(300px, auto)',
    quantifiable: 'auto',
    brands: 'auto',
    additionalFields: 'auto',
    editOrDelete: '120px',
  };

  return (
    <Stack gap="24px">
      <Stack row alignItems="center" justifyContent="space-between" gap="8px">
        <Title size="200" as="h2">
          <Trans id="settings.actions.pack-actions.title">Pack actions</Trans>
        </Title>
        <Button onPress={() => setIsDrawerOpen(true)}>
          <IconAdd />
          <Trans id="settings.actions.pack-actions.add">New pack action</Trans>
        </Button>
      </Stack>
      {!isLoading && !sortedPackActionTypes?.length ? (
        <EmptyState
          subtitle={<Trans id="settings.actions.pack-actions.none">No pack actions</Trans>}
        />
      ) : (
        <Table
          ariaLabel={t({
            id: 'settings.actions.pack-actions.table.label',
            message: 'Pack actions',
          })}
          columnWidths={Object.values(columnWidths)}
          variant="grid"
          bordered
        >
          {(isLoading || !!sortedPackActionTypes?.length) && (
            <Header>
              <Row>
                <Column>
                  <Trans id="settings.actions.pack-actions.column.name">Name</Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.pack-actions.column.product-categories">
                    Product categories
                  </Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.pack-actions.column.quantifiable">Quantifiable</Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions.column.brands">Brands</Trans>
                </Column>
                <Column>
                  <Trans id="settings.actions.actions.column.additional-info-config">
                    Additional fields
                  </Trans>
                </Column>
                <Column
                  ariaLabel={t({
                    id: 'settings.actions.pack-actions.column.edit-or-delete',
                    message: 'Edit or delete',
                  })}
                />
              </Row>
            </Header>
          )}
          <Body>
            {sortedPackActionTypes?.map((packActionType) => (
              <PackActionRow
                key={packActionType.id}
                packActionType={packActionType}
                onEdit={() => {
                  setPackActionTypeEdit(packActionType);
                  setIsDrawerOpen(true);
                }}
              />
            ))}
          </Body>
        </Table>
      )}
      <PackActionDrawer
        packActionType={packActionTypeEdit}
        isOpen={isDrawerOpen}
        onClose={() => {
          setIsDrawerOpen(false);

          // Wait for the Drawer CSS exit animation to finish
          setTimeout(() => {
            setPackActionTypeEdit(null);
          }, 150);
        }}
      />
    </Stack>
  );
};

const PackActionRow = ({
  packActionType,
  onEdit,
}: {
  packActionType: PackActionType;
  onEdit: () => void;
}) => {
  const showToast = useShowToast();

  const { t } = useLingui();
  const { _db } = useDatabaseTranslation();
  const [showActions, setShowActions] = useState(false);

  const [deleteConfirmationOpened, setDeleteConfirmationOpened] = useState(false);
  const { mutateAsync: deletePackActionType, isPending: isDeletePackActionTypePending } =
    useDeletePackActionType();

  const packActionTypeName = _db(packActionType.name);

  const sortedBrands = packActionType.brands.sort((a, b) => a.name.localeCompare(b.name));
  const brandNames = (
    <Stack>
      {sortedBrands.map((brand) => (
        <span key={brand.id}>· {brand.name}</span>
      ))}
    </Stack>
  );

  const additionalFieldsNames = (
    <Stack>
      {packActionType.additionalInfoConfig?.map((element) => (
        <span key={element.id}>· {_db(element.config.label)}</span>
      ))}
    </Stack>
  );

  return (
    <>
      <Row key={packActionType.id}>
        <Cell>
          <Stack gap="4px">
            <Stack>
              <InlineText weight="medium">{packActionTypeName}</InlineText>
              <InlineText size="200" color="disabled">
                {packActionType.id}
              </InlineText>
            </Stack>
            <Button
              variant="link-subtle"
              size="small"
              style={{ justifyContent: 'flex-start' }}
              onPress={() => setShowActions((prev) => !prev)}
            >
              <Trans id="components.services.pack-actions.view-details">View details</Trans>
              <IconChevron up={showActions} down={!showActions} />
            </Button>
            {showActions && (
              <ul>
                {packActionType.actions.map((action) => (
                  <li key={action.id} style={{ paddingBottom: '2px' }}>
                    <Stack row alignItems="center" gap="4px">
                      <InlineText size="200" color="secondary">
                        {_db(action.name)}
                      </InlineText>
                      {action.refashionId && (
                        <RefashionLogo tooltip={action.refashionId} size="medium" />
                      )}
                    </Stack>
                  </li>
                ))}
              </ul>
            )}
          </Stack>
        </Cell>
        <Cell>
          <ProductAttributeValuesHierarchy
            productAttributeValues={packActionType.productAttributeValues}
          />
        </Cell>
        <Cell justifyContent="center">
          {packActionType.isQuantifiable ? <IconCheck /> : <IconCross />}
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              packActionType.brands.length
                ? brandNames
                : t({
                    id: 'settings.actions.actions.no-brands',
                    message: 'No brands',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {packActionType.brands.length}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Tooltip
            content={
              packActionType.additionalInfoConfig?.length
                ? additionalFieldsNames
                : t({
                    id: 'settings.actions.pack-actions.no-additional-fields',
                    message: 'No additional fields',
                  })
            }
            textAlign="start"
          >
            <Button style={{ padding: '1rem' }} variant="style-less">
              {packActionType.additionalInfoConfig?.length || 0}
            </Button>
          </Tooltip>
        </Cell>
        <Cell justifyContent="center">
          <Stack row gap="0.5rem" flexWrap="nowrap">
            <Button
              variant="secondary"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.actions.pack-actions.edit', message: 'Edit' })}
              onPress={onEdit}
            >
              <IconEdit />
            </Button>
            <AgreementPricesDialog
              id={packActionType.id}
              name={_db(packActionType.name)}
              agreementPrices={packActionType.agreementPrices}
              type="pack-action"
              mode="brand"
            />
            <Button
              variant="danger"
              iconOnly
              size="small"
              ariaLabel={t({ id: 'settings.actions.pack-actions.delete', message: 'Delete' })}
              onPress={() => setDeleteConfirmationOpened(true)}
            >
              <IconTrash />
            </Button>
          </Stack>
        </Cell>
      </Row>
      <ConfirmationDialog
        title={
          <Trans id="settings.actions.pack-actions.delete.confirmation.title">
            Delete pack of actions
          </Trans>
        }
        confirm={
          <Trans id="settings.actions.pack-actions.delete.confirmation.confirm">Delete</Trans>
        }
        onConfirm={() =>
          deletePackActionType(packActionType.id)
            .then(() => {
              showToast({
                text: (
                  <Trans id="settings.actions.pack-actions.delete.confirmation.success">
                    The pack of actions <b>&quot;{packActionTypeName}&quot;</b> has been deleted.
                  </Trans>
                ),
                type: 'success',
              });
            })
            .catch(() => {
              showToast({
                text: (
                  <Trans id="settings.actions.pack-actions.delete.confirmation.error">
                    Error while deleting the pack of actions <b>&quot;{packActionTypeName}&quot;</b>
                    . Please try again later.
                  </Trans>
                ),
                type: 'error',
              });
            })
        }
        showDialog={deleteConfirmationOpened}
        setShowDialog={setDeleteConfirmationOpened}
        maxWidth="35rem"
        isPending={isDeletePackActionTypePending}
      >
        <Stack gap="0.5rem">
          <p>
            <Trans id="settings.actions.pack-actions.delete.confirmation.description.1">
              Are you sure you want to delete the pack of actions{' '}
              <b>&quot;{packActionTypeName}&quot;</b>?
            </Trans>
          </p>
          <p>
            <Trans id="settings.actions.pack-actions.delete.confirmation.description.2">
              Active requests with this pack of actions will continue to show it, but users won't be
              able to choose this pack of actions for new requests.
            </Trans>
          </p>
          <hr />
          <p>
            <b>
              <Trans id="settings.actions.pack-actions.delete.confirmation.description.3">
                This deletion cannot be undone.
              </Trans>
            </b>
          </p>
        </Stack>
      </ConfirmationDialog>
    </>
  );
};

const PackActionDrawer = ({
  packActionType,
  isOpen,
  onClose,
}: {
  packActionType: PackActionType | null;
  isOpen: boolean;
  onClose: () => void;
}) => {
  return (
    <Drawer isOpen={isOpen} onOpenChange={onClose}>
      <PackActionDrawerContent packActionType={packActionType} onClose={onClose} />
    </Drawer>
  );
};

const PackActionDrawerContent = ({
  packActionType,
  onClose,
}: {
  packActionType: PackActionType | null;
  onClose: () => void;
}) => {
  const { t } = useLingui();
  const { isMobile } = useViewPort();
  const showToast = useShowToast();
  const { _db } = useDatabaseTranslation();

  const { mutateAsync: updatePackActionType, isPending: isUpdatePackActionTypePending } =
    useUpdatePackActionType();
  const { mutateAsync: createPackActionType, isPending: isCreatePackActionTypePending } =
    useCreatePackActionType();

  const [name, setName] = useState(
    Object.fromEntries(
      SUPPORTED_LOCALES.map(({ id }) => [id, packActionType?.name[id] ?? ''])
    ) as I18nWording
  );
  const [actions, setActions] = useState(
    (packActionType?.actions.map((action) => ({
      id: action.id,
      name: Object.fromEntries(SUPPORTED_LOCALES.map(({ id }) => [id, action.name[id] ?? ''])),
      refashionId: action.refashionId,
      key: crypto.randomUUID(),
    })) as {
      key: string;
      id?: string;
      name: I18nWording;
      refashionId: RefashionAction | null;
    }[]) ?? []
  );
  const [isQuantifiable, setIsQuantifiable] = useState(packActionType?.isQuantifiable ?? true);
  const [productAttributeValues, setProductAttributeValues] = useState(
    packActionType?.productAttributeValues ?? []
  );
  const [additionalInfoConfig, setAdditionalInfoConfig] =
    useState<ActionTypeOrganizationAdditionalInfoConfig>(
      packActionType?.additionalInfoConfig || []
    );
  const [brandIds, setBrandIds] = useState(packActionType?.brands.map((brand) => brand.id) ?? []);

  const [showErrors, setShowErrors] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const nameError = hasNameError(name);
  const actionsError =
    !actions.length || actions.some((action) => isMissingTranslation(action.name));
  const additionalInfoConfigError = hasAdditionalInfoConfigError(additionalInfoConfig);

  const hasError = nameError || actionsError || additionalInfoConfigError;

  const onSave = async () => {
    setShowErrors(true);

    if (hasError) {
      return;
    }

    try {
      const body = {
        name: trimI18nWording(name),
        isQuantifiable,
        productAttributeValues: productAttributeValues.map(({ id }) => id),
        actions: actions.map((action) => ({
          id: action.id,
          name: action.name,
          refashionId: action.refashionId,
        })),
        additionalInfoConfig,
        brandIds,
      };

      if (packActionType) {
        await updatePackActionType({
          packActionTypeId: packActionType.id,
          ...body,
        });
      } else {
        await createPackActionType({
          ...body,
          agreementPrices: [],
        });
      }

      onClose();

      const packActionName = _db(name);

      showToast({
        text: (
          <Trans id="settings.actions.pack-actions.drawer.success">
            Pack action &quot;{packActionName}&quot; successfully saved
          </Trans>
        ),
      });
    } catch (err: any) {
      console.error(err);
      setApiError(
        (err.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <DrawerHeader>
        <Title size="300">
          {packActionType ? (
            <Trans id="settings.actions.pack-actions.drawer.title">Update pack action</Trans>
          ) : (
            <Trans id="settings.actions.pack-actions.drawer.title-create">Create pack action</Trans>
          )}
        </Title>
      </DrawerHeader>

      <DrawerBody>
        <Stack padding={isMobile ? '1rem' : '2rem'} gap="1rem">
          <NameInputs name={name} setName={setName} showErrors={showErrors} />

          <Stack gap="0.5rem">
            <p className="label-100-medium">
              <Trans id="settings.actions.pack-actions.drawer.actions">Actions</Trans>
            </p>

            {actions.map((action) => {
              return (
                <Box key={action.key} gap="0.5rem" padding="8px">
                  <NameInputs
                    name={action.name}
                    setName={(name) =>
                      setActions(
                        actions.map((prevAction) =>
                          prevAction.key === action.key ? { ...prevAction, name } : prevAction
                        )
                      )
                    }
                    showErrors={showErrors}
                  />
                  <RefashionIdSelect
                    refashionId={action.refashionId}
                    setRefashionId={(refashionId) =>
                      setActions(
                        actions.map((prevAction) =>
                          prevAction.key === action.key
                            ? { ...prevAction, refashionId }
                            : prevAction
                        )
                      )
                    }
                  />
                  <Button
                    size="medium"
                    variant="secondary-danger"
                    onPress={() => setActions(actions.filter((a) => a.key !== action.key))}
                    style={{ alignSelf: 'flex-start' }}
                  >
                    <IconTrash />
                    <Trans id="settings.actions.pack-actions.drawer.actions.delete">Remove</Trans>
                  </Button>
                </Box>
              );
            })}

            <div>
              <Button
                variant="link-subtle"
                size="small"
                onPress={() => {
                  setActions([
                    ...actions,
                    {
                      key: crypto.randomUUID(),
                      name: Object.fromEntries(
                        SUPPORTED_LOCALES.map(({ id }) => [id, ''])
                      ) as I18nWording,
                      refashionId: null,
                    },
                  ]);
                }}
                style={
                  showErrors && !actions.length ? { color: 'var(--color-danger-700)' } : undefined
                }
              >
                <IconAdd />
                <Trans id="settings.actions.pack-actions.drawer.actions.add">Add an action</Trans>
              </Button>
            </div>
          </Stack>

          <IsQuantifiableRadio
            isQuantifiable={isQuantifiable}
            setIsQuantifiable={setIsQuantifiable}
          />

          <BrandsMultiSelect selectedBrandIds={brandIds} setBrandIds={setBrandIds} />

          <ProductAttributeValuesMultiSelects
            productAttributeValues={productAttributeValues}
            setProductAttributeValues={setProductAttributeValues}
          />

          <AdditionalInfoConfigInputs
            additionalInfoConfig={additionalInfoConfig}
            setAdditionalInfoConfig={setAdditionalInfoConfig}
            showErrors={showErrors}
          />
        </Stack>
      </DrawerBody>

      <Footer>
        {(apiError || (showErrors && hasError)) && (
          <FooterError>
            {!apiError && showErrors && hasError ? (
              <Trans id="settings.actions.pack-actions.drawer.error">
                Please fill the missing information
              </Trans>
            ) : (
              apiError
            )}
          </FooterError>
        )}

        <FooterButtons>
          <Button variant="secondary" size="medium" onPress={onClose}>
            <Trans id="settings.actions.pack-actions.drawer.cancel">Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            size="medium"
            onPress={onSave}
            isLoading={isUpdatePackActionTypePending || isCreatePackActionTypePending}
          >
            <Trans id="settings.actions.pack-actions.drawer.save">Save</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
